import React, { memo, useCallback, useState } from 'react'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import type { Customer } from 'src/interfaces/customer'
import { resolveCustomerDisplayName } from 'src/utils/customerList'

interface Props {
  customer: Customer
}

const resolveInitials = (name: string): string => {
  const parts = name.trim().split(/\s+/).slice(0, 2)

  return parts
    .map((part) => {
      return part.charAt(0).toUpperCase()
    })
    .join('')
}

const CustomerLogoCellComponent = ({ customer }: Props) => {
  // `logoUrl` is a signed URL regenerated per response, so it can be expired or dead. Track the
  // URL that failed rather than a boolean: DataGrid reuses cell components across rows while
  // scrolling, and a boolean would leave a later row stuck on the placeholder.
  const [failedLogoUrl, setFailedLogoUrl] = useState<string | null>(null)

  const handleImageError = useCallback(() => {
    setFailedLogoUrl(customer.logoUrl)
  }, [customer.logoUrl])

  const displayName = resolveCustomerDisplayName(customer)
  const canShowImage = Boolean(customer.logoUrl) && customer.logoUrl !== failedLogoUrl

  return (
    // Full height so the 28px badge sits on the row's vertical centre, and `overflow-hidden` so a
    // broken image's alt text can never overflow the 72px column and shift the layout.
    <div css={tw`flex h-full w-full items-center justify-center overflow-hidden`}>
      {canShowImage ? (
        <img
          src={customer.logoUrl as string}
          alt={`${displayName} logo`}
          css={tw`h-7 w-7 shrink-0 rounded-md object-cover`}
          onError={handleImageError}
        />
      ) : (
        <div
          css={tw`flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-primary-100`}
          aria-label={`${displayName} logo placeholder`}
        >
          <Typography variant="subtitle2" css={tw`text-primary-700`}>
            {resolveInitials(displayName)}
          </Typography>
        </div>
      )}
    </div>
  )
}

export const CustomerLogoCell = memo(CustomerLogoCellComponent)
